import {motion} from 'framer-motion';
import {
  Box,
  CSSDimension,
  HStack,
  Icon,
  Integer,
  Show,
  Text,
  ThemeColorPath,
  ThemeIconKey,
  ValueByDevice,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {ReactNode} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TestIdProps} from 'shared/web';

import {OptionType} from '../../types/FormControlProps';

export type SegmentItemBaseProps<T> = {
  variant?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  minWidth?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer>;
  leftIcon?: ThemeIconKey;
  leftIconColor?: ThemeColorPath;
  rightIcon?: ThemeIconKey;
  rightIconColor?: ThemeColorPath;
  rightIconLabel?: string;
} & OptionType<T, string | ReactNode>;

export interface SegmentItemProps<T> extends SegmentItemBaseProps<T>, TestIdProps {
  isSelected: boolean;
  onClick?: (value: T) => void;
  id: string;
}

export function SegmentItem<T extends string>(props: SegmentItemProps<T>) {
  function handleClick() {
    if (!props.isDisabled) {
      props.onClick?.(props.value);
    }
  }

  return (
    <div
      onClick={handleClick}
      data-testid={props['data-testid']}
      key={props.value}
      className={cn(
        segmentItemVariants({
          isDisabled: props.isDisabled ?? false,
          isFocused: props.isFocused ?? false,
        })
      )}
      role="radio"
      aria-checked={props.isSelected}
      aria-disabled={props.isDisabled}
      aria-label={
        typeof props.label === 'string'
          ? [props.label, props.rightIconLabel].filter(isNotNilOrEmpty).join(' · ')
          : `Option ${props.value}`
      }
      id={`${props.id}-${props.value}`}
      tabIndex={-1}
    >
      <Show when={props.isSelected}>
        <motion.div className={cn(activeBackgroundVariants())} />
      </Show>

      <div className={cn(labelWrapperVariants())}>
        <Box
          paddingVertical={1}
          paddingHorizontal={2}
          width="100%"
          minWidth={props.minWidth ?? undefined}
        >
          <HStack spacing={1} align="center" justify="center">
            <Show when={props.leftIcon}>
              <span aria-hidden="true">
                <Icon value={props.leftIcon} color={props.leftIconColor} size={4} />
              </span>
            </Show>
            <Text size="xSmall" align="center">
              {props.label}
            </Text>
            <Show when={props.rightIcon}>
              <span aria-hidden="true">
                <Icon value={props.rightIcon} color={props.rightIconColor} size={4} />
              </span>
            </Show>
          </HStack>
        </Box>
      </div>
    </div>
  );
}

const labelWrapperVariants = cva('z-[2] w-full');

const activeBackgroundVariants = cva(
  'absolute left-0 top-0 z-[1] h-full w-full rounded-[var(--radius-small)] border border-solid border-[var(--color-palettes-neutral-40-100)] bg-[var(--color-white)] [box-shadow:var(--shadow-elevation-2)]'
);

const segmentItemVariants = cva(
  'relative flex grow rounded-[var(--radius-small)] bg-[var(--color-palettes-neutral-20-100)] p-px text-center [outline:none] hover:bg-[var(--color-palettes-neutral-40-100)]',
  {
    variants: {
      isDisabled: {
        true: 'cursor-not-allowed opacity-60',
        false: 'cursor-pointer opacity-100',
      },
      isFocused: {
        true: '[outline:var(--outline-default)_var(--color-accent)] [outline-offset:var(--outline-offset)]',
        false: '',
      },
    },
  }
);
